import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { CTASection } from "@/components/site/CTASection";
import { fetchPosts } from "@/lib/api";
import { Seo } from "@/components/site/Seo";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function InsightsPage() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchPosts({ limit: 30 })
      .then((d) => { if (alive) setPosts(d); })
      .catch((e) => { if (alive) { setPosts([]); setError(e?.message || "error"); } });
    return () => { alive = false; };
  }, [i18n.resolvedLanguage]);

  // Compute the list body up-front so the JSX below stays flat and readable —
  // no nested ternaries. Three tri-state branches: loading / empty / data.
  let listContent;
  if (posts === null) {
    listContent = (
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-72 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
        ))}
      </div>
    );
  } else if (posts.length === 0) {
    listContent = (
      <div data-testid="insights-empty" className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-blue-700"><BookOpen className="h-6 w-6" /></div>
        <h2 className="mt-4 text-xl font-extrabold text-slate-900">{t("pages.insightsEmpty")}</h2>
        <p className="mt-2 text-sm text-slate-600">{error ? "" : ""}</p>
        <Link to="/contact" className="mt-5 inline-block"><Button className="h-10 rounded-lg bg-blue-600 px-4 text-white hover:bg-blue-700">{t("cta.primary")}<ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
      </div>
    );
  } else {
    listContent = (
      <div data-testid="insights-grid" className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((p, idx) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.3, delay: idx * 0.03 }}
            className="group overflow-hidden rounded-2xl border border-slate-200 bg-white hover:border-slate-300">
            <Link to={`/blog/${p.slug}`} data-testid={`insight-card-${p.slug}`}>
              {p.cover_image ? (
                <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100">
                  <img src={p.cover_image} alt={`${p.title} — cover image`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
              ) : (
                <div className="aspect-[16/9] w-full bg-gradient-to-br from-blue-50 via-white to-emerald-50" />
              )}
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-blue-700">
                  <span>{p.category || t("pages.insightCategory")}</span>
                  <span className="text-slate-300">•</span>
                  <span className="flex items-center gap-1 text-slate-500"><Calendar className="h-3.5 w-3.5" /> {formatDate(p.created_at)}</span>
                </div>
                <h3 className="mt-3 text-lg font-extrabold text-slate-900 group-hover:text-blue-700">{p.title}</h3>
                <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.excerpt}</p>
                <span className="mt-3 inline-flex items-center text-sm font-semibold text-blue-700">{t("cta.read")} <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="insights-page">
      <Seo
        title={t("seo.blog.metaTitle")}
        description={t("seo.blog.metaDescription")}
        keywords={t("seo.blog.keywords")}
        path="/blog"
        breadcrumbs={[
          { name: t("seo.breadcrumbHome"), url: "/" },
          { name: t("seo.blog.breadcrumbLabel"), url: "/blog" },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">
            {t("pages.insightsTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("pages.insightsSubtitle")}</p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8">
        {listContent}
      </section>
      <CTASection />
    </div>
  );
}
